/**
 * 🔴 **SIGNING IN THROUGH BETTER AUTH** (D84) — the shell's side of it.
 *
 * Devin: *"I think we should handle the auth with better-auth… we going to use the full opensoure
 * version."*
 *
 * 🔴 **THE LONG-LIVED CREDENTIAL IS A COOKIE NOW, AND THAT IS AN UPGRADE.**
 *
 * D83 built a refresh token, kept it in `localStorage`, rotated it single-use and wrote down honestly
 * that a readable copy on the origin was the price of staying signed in for a day. Better Auth's
 * session cookie is **httpOnly** — script on this origin cannot read the COOKIE — and it carries the
 * same 24-hour-to-3-day life. So the thing D83 conceded is simply no longer conceded, and the
 * machinery that made it safe (rotation, revocation, an absolute end) is the library's now.
 *
 * 🔴 **AND THE COOKIE BEING UNREADABLE IS NOT THE WHOLE STORY** (D89). Better Auth's `list-sessions`
 * route handed script the raw session token, which with the `bearer` plugin replayed as a full
 * credential from anywhere — the httpOnly protection undone by a different door. Both are closed:
 * `services/auth/identity.ts` serves only the four routes this product calls, and `bearer` is gone.
 * The claim above is true as the app is now configured, and it was not when it was first written.
 *
 * What is still held in `localStorage` is the fifteen-minute ACCESS token, and only because the MFEs
 * are handed a bearer token rather than a cookie jar (`state/globalState.ts`). A copy of that is
 * worth fifteen minutes, which is the trade a short access token exists to make.
 *
 * 🔴 **`credentials: "include"` ON EVERY CALL HERE.** The cookie is what makes the session; a fetch
 * that omits it succeeds at signing in and then cannot renew, which shows up half an hour later as
 * "it signs me out" and looks exactly like the bug D83 was written to fix.
 */
import { API_BASE_URL } from "@/api/base";
import type { FetchLike } from "./http";

/** The prefix Better Auth owns on the API (`services/auth/identity.ts`). The two must stay equal. */
export const IDENTITY_BASE = "/identity";
export const SIGN_IN_PATH = `${IDENTITY_BASE}/sign-in/email`;
export const TOKEN_PATH = `${IDENTITY_BASE}/token`;
export const SIGN_OUT_PATH = `${IDENTITY_BASE}/sign-out`;


/**
 * 🔴 The issuer Better Auth stamps (D84) — `IDENTITY_ISSUER` in `services/auth/identity-session.ts`,
 * and the two must stay equal.
 *
 * A NAME, not a URL: Better Auth would otherwise default `iss` to the deployment's own base URL,
 * which differs between a laptop, the dev host and production, so a token minted in one environment
 * would be unrecognisable in the next.
 */
export const IDENTITY_ISSUER = "pinksy-identity";
export const IDENTITY_JWKS_PATH = `${IDENTITY_BASE}/jwks`;

/**
 * Whether a token was signed by Better Auth, read from its own `iss` claim without verifying.
 *
 * 🔴 Unverified is safe here for the same reason `jwks.ts` reads `kid` unverified: it selects WHICH
 * public keys to try, and the signature still has to verify against them. What it buys is a real
 * failure message — a token tried against another issuer's keys reports "bad signature" when the
 * truth is "wrong issuer", and somebody then looks for a key rotation that never happened.
 *
 * 🔴 **TWO SHAPES, BECAUSE JOSE HANDS ITS KEY RESOLVER A JWS OBJECT, NOT A STRING.**
 * `JWTVerifyGetKey` is called as `(protectedHeader, token)` where `token` is a `FlattenedJWSInput` —
 * `{ payload, protected, signature }` — whose `payload` is the base64url segment on its own, while
 * every other caller has the compact `a.b.c` string. Handling only the string sends every Better
 * Auth token to the PASSPORT's keys: a correct password, a 200 from the server, and "Sign-in did not
 * complete" on screen with nothing wrong anywhere.
 *
 * 🔴 It lives HERE rather than in `jwks.ts` because it is a fact about a TOKEN, not about a key
 * store — and because `jwks.ts` is mocked wholesale by the boot and session tests, so a function
 * hidden behind that mock is `undefined` at the moment `verify.ts` calls it.
 */
export const isIdentityToken = (token: unknown): boolean => {
  const segment =
    typeof token === "string"
      ? token.split(".")[1]
      : typeof (token as { payload?: unknown })?.payload === "string"
        ? (token as { payload: string }).payload
        : undefined;
  if (segment === undefined || segment === "") return false;
  try {
    const decoded = JSON.parse(atob(segment.replace(/-/g, "+").replace(/_/g, "/"))) as { iss?: unknown };
    // 🔴 D133: the issuer became a URL (`https://<desk>/identity`); the bare word is the one minted before that deploy.
    return decoded.iss === IDENTITY_ISSUER || (typeof decoded.iss === "string" && decoded.iss.endsWith("/identity"));
  } catch {
    return false;
  }
};

/** Long enough for a scrypt verification on a loaded server, short enough that a hang is not a hung screen. */
export const SIGN_IN_TIMEOUT_MS = 20_000;

export interface IdentitySignedIn {
  ok: true;
  /** The fifteen-minute EdDSA JWT the gate and the MFEs take. */
  token: string;
  userId: string;
  displayName: string | null;
  email: string;
}

export interface IdentityFailed {
  ok: false;
  message: string;
}

export type IdentityOutcome = IdentitySignedIn | IdentityFailed;

interface SignInBody {
  user?: { id?: unknown; name?: unknown; email?: unknown };
}

interface TokenBody {
  token?: unknown;
}

interface ErrorBody {
  message?: unknown;
}

const text = (value: unknown): string | null => (typeof value === "string" && value.trim() !== "" ? value.trim() : null);

/**
 * 🔴 **THE ACCESS TOKEN, FROM THE COOKIE SESSION.** `GET /identity/token` mints a fresh
 * fifteen-minute JWT for whoever the cookie says you are.
 *
 * This is the whole renewal story now, and it is smaller than the one it replaces: there is no
 * refresh token to present, rotate, store or lose — the browser holds an httpOnly cookie and this
 * call turns it into a bearer token whenever one is needed. Returns null when the session has ended,
 * which is the signal to show the sign-in screen.
 */
export async function fetchIdentityToken(base: string = API_BASE_URL, fetchImpl?: FetchLike): Promise<string | null> {
  const send = fetchImpl ?? fetch;
  try {
    const response = await send(`${base}${TOKEN_PATH}`, { credentials: "include" });
    if (!response.ok) return null;
    const body = (await response.json()) as TokenBody;
    return text(body.token);
  } catch {
    return null;
  }
}

/**
 * An address and a password, verified by Better Auth against `authAccounts` — where the hash is the
 * one Pinksy already had (D84), so nobody's password changed.
 *
 * 🔴 The server's own refusal is passed through UNCHANGED. It says the same thing for a wrong
 * password and an address nobody has, on purpose; a client that helpfully rewrote it into "no such
 * user" would hand back the membership oracle the endpoint withholds.
 */
export async function signInWithIdentity(email: string, password: string, base: string = API_BASE_URL, fetchImpl?: FetchLike): Promise<IdentityOutcome> {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), SIGN_IN_TIMEOUT_MS);
  const send = fetchImpl ?? fetch;
  try {
    const response = await send(`${base}${SIGN_IN_PATH}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email, password }),
      credentials: "include",
      signal: abort.signal,
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as ErrorBody;
      return { ok: false, message: text(body.message) ?? "sign-in was refused" };
    }
    const body = (await response.json()) as SignInBody;
    // 🔴 The sign-in answer carries the SESSION token, not the JWT — a different credential with a
    // different shape. The bearer token comes from `/identity/token`, which is also the call every
    // renewal makes, so the same path is exercised from the first second of the session.
    const token = await fetchIdentityToken(base, fetchImpl);
    if (token === null) return { ok: false, message: "the server answered without a session" };
    return {
      ok: true,
      token,
      userId: text(body.user?.id) ?? "",
      displayName: text(body.user?.name),
      email: text(body.user?.email) ?? email,
    };
  } catch (error) {
    // An abort and a dead connection read the same to a person: the app could not reach the server.
    return { ok: false, message: (error as Error)?.name === "AbortError" ? "the server did not answer in time" : "could not reach the server" };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * End the session on the server — the cookie is cleared and the row revoked.
 *
 * Best-effort and never awaited by the UI: signing out is immediate on screen whatever the network
 * does, and a session that survives because the person was offline lapses on its own.
 */
export async function signOutIdentity(base: string = API_BASE_URL, fetchImpl?: FetchLike): Promise<boolean> {
  const send = fetchImpl ?? fetch;
  try {
    const response = await send(`${base}${SIGN_OUT_PATH}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: "{}",
      credentials: "include",
    });
    return response.ok;
  } catch {
    return false;
  }
}
